
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import apiClient from '../../../lib/api';
import { resolveImageUrl } from '../../../lib/utils';

interface Slide {
  id: string;
  slide_number: number;
  title: string;
  subtitle: string;
  description: string;
  image_url: string;
  cta_text: string;
  cta_link: string;
}

interface HeroSliderProps {
  settings?: {
    hero_title?: string;
    hero_subtitle?: string;
  };
}

const HeroSlider = ({ settings }: HeroSliderProps) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [slides, setSlides] = useState<Slide[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSlides();
  }, []);

  const loadSlides = async () => {
    try {
      setLoading(true);
      const slidesData = await apiClient.getHeroSlides();
      setSlides(slidesData.sort((a: Slide, b: Slide) => a.slide_number - b.slide_number));
    } catch (error) {
      console.error('Failed to load slides:', error);
      // Set empty slides if API fails - admin should add slides through admin panel
      setSlides([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (slides.length > 0) {
      const timer = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % slides.length);
      }, 5000);

      return () => clearInterval(timer);
    }
  }, [slides.length]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
  };

  if (loading) {
    return (
      <div className="relative h-screen overflow-hidden bg-gray-900 flex items-center justify-center">
        <div className="text-center text-white">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-400 mx-auto mb-4"></div>
          <p>Loading carousel...</p>
        </div>
      </div>
    );
  }

  if (slides.length === 0) {
    return (
      <div className="relative h-screen overflow-hidden bg-gray-900 flex items-center justify-center">
        <div className="text-center text-white">
          <h1 className="text-4xl md:text-6xl font-bold mb-4">Welcome to Fully A/C Salon</h1>
          <p className="text-xl text-gray-300">Your premier destination for luxury grooming</p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative h-screen overflow-hidden">
      {/* Slides */}
      {slides.map((slide, index) => (
        <div
          key={slide.id}
          className={`absolute inset-0 transition-transform duration-1000 ease-in-out ${index === currentSlide ? 'translate-x-0' :
            index < currentSlide ? '-translate-x-full' : 'translate-x-full'
            }`}
        >
          {/* Background Image */}

          {/* Background Image */}
          <div
            className="absolute inset-0 bg-cover bg-center bg-no-repeat"
            style={{ backgroundImage: `url("${resolveImageUrl(slide.image_url)}")` }}
          >
            <div className="absolute inset-0 bg-black/60"></div>
          </div>

          {/* Content */}
          <div className="relative z-10 h-full flex items-center">
            <div className="container w-full">
              <div className="max-w-2xl">
                <div className="space-y-6 animate-fadeInUp">
                  <h2 className="text-primary-400 text-xl md:text-2xl font-semibold tracking-wide">
                    {slide.subtitle}
                  </h2>
                  <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-white leading-tight text-shadow-luxury">
                    {slide.title}
                  </h1>
                  <p className="text-lg md:text-xl text-gray-200 leading-relaxed max-w-xl">
                    {slide.description}
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4 pt-4">
                    <Link
                      to={slide.cta_link}
                      className="gold-gradient hover:bg-primary-600 text-black px-8 py-4 rounded-full font-bold text-lg transition-all duration-300 btn-hover-effect shadow-glow whitespace-nowrap cursor-pointer inline-block text-center"
                    >
                      {slide.cta_text}
                    </Link>
                    <button
                      onClick={() => window.open('tel:+917484984048', '_self')}
                      className="border-2 border-primary-400 text-primary-400 hover:bg-primary-400 hover:text-black px-8 py-4 rounded-full font-bold text-lg transition-all duration-300 whitespace-nowrap cursor-pointer"
                    >
                      Call Now
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      ))}

      {/* Navigation Arrows */}
      <button
        onClick={prevSlide}
        className="absolute left-4 top-1/2 -translate-y-1/2 z-20 w-12 h-12 bg-black/50 hover:bg-black/70 text-white rounded-full flex items-center justify-center transition-all duration-300 cursor-pointer"
      >
        <i className="ri-arrow-left-line text-xl"></i>
      </button>
      <button
        onClick={nextSlide}
        className="absolute right-4 top-1/2 -translate-y-1/2 z-20 w-12 h-12 bg-black/50 hover:bg-black/70 text-white rounded-full flex items-center justify-center transition-all duration-300 cursor-pointer"
      >
        <i className="ri-arrow-right-line text-xl"></i>
      </button>

      {/* Slide Indicators */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex space-x-3">
        {slides.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className={`w-3 h-3 rounded-full transition-all duration-300 cursor-pointer ${index === currentSlide ? 'bg-primary-400 scale-125' : 'bg-white/50 hover:bg-white/70'
              }`}
          />
        ))}
      </div>
    </div>
  );
};

export default HeroSlider;
